import { formatHumanName, type MedplumClient } from '@medplum/core'
import { DocumentReference, Patient, Coverage } from '@medplum/fhirtypes'
import { InsuranceCardDocsState, InsuranceCardSide, InsuranceRank } from '../types/insuranceTypes'
import {
  extractCoverageInsuranceCardReferences,
  getResourceIdFromReference,
} from './insuranceCardUtils'

export const getDocumentAttachmentUrl = (doc: DocumentReference): string | undefined => {
  if (!doc.content) {
    return undefined
  }

  for (const content of doc.content) {
    const url = content.attachment?.url
    if (url) {
      return url
    }
  }

  return undefined
}

export async function resolveCoverageInsuranceCardImageUrls(
  medplum: MedplumClient,
  coverage: Coverage | null,
): Promise<Record<InsuranceCardSide, string | null>> {
  if (!coverage) {
    return { front: null, back: null }
  }

  const references = extractCoverageInsuranceCardReferences(coverage)
  if (!references.front && !references.back) {
    return { front: null, back: null }
  }

  const results = await Promise.all(
    (['front', 'back'] as const).map(async (side) => {
      const reference = references[side]
      if (!reference) {
        return [side, null] as const
      }

      const documentId = getResourceIdFromReference(reference)
      if (!documentId) {
        return [side, null] as const
      }

      try {
        const document = await medplum.readResource('DocumentReference', documentId)
        return [side, getDocumentAttachmentUrl(document) ?? null] as const
      } catch {
        return [side, null] as const
      }
    }),
  )

  return results.reduce<Record<InsuranceCardSide, string | null>>(
    (acc, [side, url]) => ({ ...acc, [side]: url }),
    { front: null, back: null },
  )
}

export const uploadInsuranceImage = async (
  medplum: MedplumClient,
  file: File,
  side: InsuranceCardSide,
  patient: Patient,
  rank: InsuranceRank,
): Promise<DocumentReference> => {
  const binary = await medplum.createBinary({ data: file, contentType: file.type })
  const displayName = patient.name?.[0] ? formatHumanName(patient.name[0]) : undefined
  const sideLabel = side === 'front' ? 'Front' : 'Back'
  const descriptionSuffix = `${rank} insurance card ${sideLabel.toLowerCase()}`
  const documentInput: DocumentReference = {
    resourceType: 'DocumentReference',
    status: 'current',
    docStatus: 'final',
    subject: {
      reference: `Patient/${patient.id}`,
      display: displayName,
    },
    description: descriptionSuffix,
    category: [
      {
        coding: [
          {
            system: 'https://behuman.health/document-category',
            code: 'insurance-card',
            display: 'Insurance Card',
          },
        ],
        text: 'Insurance Card',
      },
    ],
    type: {
      coding: [
        {
          system: 'https://behuman.health/document-type',
          code: side === 'front' ? 'InsuranceCardFront' : 'InsuranceCardBack',
          display: `Insurance Card ${sideLabel}`,
        },
      ],
      text: `Insurance Card ${sideLabel}`,
    },
    content: [
      {
        attachment: {
          contentType: file.type,
          url: `Binary/${binary.id}`,
          title: `${descriptionSuffix} - ${patient.id}`,
        },
      },
    ],
  }
  return medplum.createResource<DocumentReference>(documentInput)
}

export async function buildInsuranceDocuments(
  medplum: MedplumClient,
  coverages: Coverage[],
  getInsuranceRankByOrder: (order?: number) => InsuranceRank,
): Promise<InsuranceCardDocsState> {
  type FetchResult = { rank: InsuranceRank; side: InsuranceCardSide; doc: DocumentReference }
  const tasks: Promise<FetchResult | null>[] = []
  coverages.forEach((coverage) => {
    const rank = getInsuranceRankByOrder(coverage.order)
    const references = extractCoverageInsuranceCardReferences(coverage)
    Object.keys(references).forEach((side) => {
      const reference = references[side as InsuranceCardSide]
      const documentId = getResourceIdFromReference(reference)
      if (!documentId) return
      tasks.push(
        medplum
          .readResource('DocumentReference', documentId)
          .then((doc: DocumentReference) => ({ rank, side: side as InsuranceCardSide, doc }))
          .catch(() => null),
      )
    })
  })
  if (!tasks.length) return {}
  const resolved = await Promise.all(tasks)
  const docsState: InsuranceCardDocsState = {}
  resolved.forEach((entry) => {
    if (!entry) return
    docsState[entry.rank] = {
      ...(docsState[entry.rank] || {}),
      [entry.side]: entry.doc,
    }
  })
  return docsState
}
